import { ReactNode } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Divider,
	FormControlLabel,
	Stack,
	Switch,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowDonwIcon, DeleteIcon } from "../../../../../../../../assets/icons";

interface DashboardAccordionProps {
	title: string;
	count: number;
	children: ReactNode;
	defaultExpanded?: boolean;
	onClearDashboard?: VoidFunction;
	advancedMode?: boolean;
	onSwitchChange?: VoidFunction;
	readonly?: boolean;
}

export const DashboardAccordion = ({
	title,
	count,
	children,
	defaultExpanded = true,
	onClearDashboard,
	advancedMode,
	onSwitchChange,
	readonly,
}: DashboardAccordionProps) => {
	const { t } = useTranslation("common");
	const theme = useTheme();

	return (
		<Accordion defaultExpanded={defaultExpanded} sx={{ border: `1px solid ${theme.palette.divider}` }}>
			<AccordionSummary
				expandIcon={<ArrowDonwIcon />}
				sx={{
					px: 2,
					borderBottom: `2px solid ${theme.palette.divider}`,
					borderRadius: 0.5,
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography variant="body2">{title}</Typography>

					<Box
						sx={{
							minWidth: 22,
							height: 22,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: theme.palette.grey[200],
							px: 0.5,
						}}
					>
						<Typography variant="caption" color="primary" fontWeight={700}>
							{count}
						</Typography>
					</Box>
				</Stack>
			</AccordionSummary>

			<AccordionDetails sx={{ p: 2 }}>{children}</AccordionDetails>
			{!readonly && (
				<>
					<Divider sx={{ mx: 2 }} />
					<Stack direction={"row"} justifyContent={onSwitchChange ? "space-between" : "flex-end"} p={2}>
						{onSwitchChange && (
							<Stack direction="row" justifyContent="flex-end" mt={2}>
								<Tooltip title={advancedMode ? t("common:advanced mode enabled text") : ""}>
									<span>
										<FormControlLabel
											control={
												<Switch
													color="primary"
													checked={advancedMode}
													disabled={advancedMode}
													onChange={onSwitchChange}
												/>
											}
											label={<Typography variant="body2">{t("common:advanced mode")}</Typography>}
											labelPlacement="start"
											sx={{ m: 0 }}
										/>
									</span>
								</Tooltip>
							</Stack>
						)}
						<Button
							color="error"
							startIcon={<DeleteIcon color="error" />}
							onClick={onClearDashboard}
							size="small"
						>
							{t("common:remove all")}
						</Button>
					</Stack>
				</>
			)}
		</Accordion>
	);
};
